import React, { useContext, useState, useMemo, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import SignatureCanvas from "react-signature-canvas";

// Context
import { CoreContext } from "../../context";

// Hooks
import {
  useSignatureCanvas,
  useTimesheetSubmission,
  usePayoutShiftManagement,
} from "../../hooks/timekeeping";

// Features
import Title from "../../features/timekeeping/payoutTimesheet/Title";
import PayoutShifts from "../../features/timekeeping/payoutTimesheet/PayoutShifts";
import Checklist from "../../features/timekeeping/payoutTimesheet/Checklist";
import { Button } from "../../components/ui";
import { ModalWrapper } from "../../components/ui";
import { Spinner } from "../../components/ui";
import { WarningBanner } from "../../components/ui";

// Components
import usePayoutTimesheetCtx, {
  PayoutTimesheetContext,
} from "../../context/page/PayoutTimesheetContext";
import { BasicCard } from "../../components/ui";

// Utils
import { getPayoutReviewBanner } from "../../utils";

// API
import { savePayoutTimesheet } from "../../services/api/timekeeping";

const PAYOUT_DASHBOARD_REFRESH_CHANNEL = "payout-timesheet-dashboard-refresh";
const PAYOUT_DASHBOARD_REFRESH_EVENT = "payout-timesheet-saved";

/**
 * Notifies open payout dashboards that a payout timesheet was saved.
 *
 * @param {Object} payload - Saved payout timesheet metadata.
 * @param {number|string} payload.workerId - Saved worker id.
 * @param {string} payload.weekEnding - Saved week ending.
 * @returns {void}
 */
const notifyPayoutDashboardRefresh = ({ workerId, weekEnding }) => {
  const eventPayload = {
    type: PAYOUT_DASHBOARD_REFRESH_EVENT,
    workerId,
    weekEnding,
    timestamp: Date.now(),
  };

  if ("BroadcastChannel" in window) {
    const channel = new BroadcastChannel(PAYOUT_DASHBOARD_REFRESH_CHANNEL);
    channel.postMessage(eventPayload);
    channel.close();
    return;
  }

  localStorage.setItem(
    PAYOUT_DASHBOARD_REFRESH_CHANNEL,
    JSON.stringify(eventPayload)
  );
};

/**
 * PayoutTimesheet component renders the final payroll review for a worker timesheet.
 *
 * Please do not import this component directly. Instead, use it as a template/example
 * for creating new components.
 *
 * @param {Object} props - The properties for the component.
 * * @param {string} props.className - Additional CSS classes for custom styling.
 * * @param {string} props.text - The text content to display inside the component.
 */
const PayoutTimesheet = () => {
  const { userId, weekEnding: urlWeekEnding } = useParams();
  const payoutTimesheetCtx = usePayoutTimesheetCtx(
    userId ? parseInt(userId) : null,
    urlWeekEnding
  );
  const navigate = useNavigate();
  const { appName } = useContext(CoreContext);
  const {
    payoutTimesheetDays,
    setPayoutTimesheetDays,
    hoursConflicts,
    payoutComments,
    payoutCommentText,
    setPayoutCommentText,
    reviewStatus,
    weekEnding,
    workerSignature,
    worker,
  } = payoutTimesheetCtx;
  // Start as editing if there are initial conflicts
  const [isEditing, setIsEditing] = useState(hoursConflicts);
  const [isSubmittable, setIsSubmittable] = useState(false);
  const [submissionResult, setSubmissionResult] = useState(null);

  // Shift management hook
  const { handleShiftsUpdate, addShiftToDay, removeShiftFromDay } =
    usePayoutShiftManagement(setPayoutTimesheetDays);

  // Signature canvas hook
  const {
    signatureRef: payoutSignatureRef,
    signatureContainerRef,
    isSigned: isPayoutSigned,
    clearSignature,
    getSignatureData,
    validateSignature,
    signatureCanvasOptions,
    signatureCanvasCanvasProps,
    onSignatureEnd,
  } = useSignatureCanvas();

  // Timesheet submission hook
  const {
    isSubmitting,
    showSuccessModal,
    handleSubmit: handleSubmitTimesheetBase,
    closeSuccessModal,
  } = useTimesheetSubmission({
    submitFunction: savePayoutTimesheet,
    onSuccess: (response) => {
      setSubmissionResult(response);
      notifyPayoutDashboardRefresh({
        workerId: worker?.id,
        weekEnding,
      });
    },
    onError: (error) => {
      alert(error);
    },
  });

  // Handle payout comments change.
  const handleCommentsChange = (e) => {
    const comments = e.target.value;
    setPayoutCommentText(comments);
    payoutComments.current = comments;
  };

  const handleSuccessModalClose = () => {
    closeSuccessModal();
    setSubmissionResult(null);
    navigate("/timekeeping/payout-timesheets");
  };

  // Calculate unique job numbers from all shifts
  const uniqueJobNumbers = useMemo(() => {
    const jobSet = new Set();
    payoutTimesheetDays.forEach((day) => {
      day.shifts?.forEach((shift) => {
        // Use jobPayout if it exists (manager override), otherwise use job
        const jobId = shift.jobPayout || shift.job;
        if (jobId) {
          jobSet.add(jobId);
        }
      });
    });
    return Array.from(jobSet);
  }, [payoutTimesheetDays]);

  const maxJobsExceeded = uniqueJobNumbers.length > 5;
  const reviewBanner = useMemo(
    () => getPayoutReviewBanner(reviewStatus),
    [reviewStatus]
  );

  // Update submittable state based on conditions
  useEffect(() => {
    // Check if all requirements are met to enable submission
    if (isPayoutSigned && !isEditing && !isSubmitting && !maxJobsExceeded) {
      setIsSubmittable(true);
    } else {
      setIsSubmittable(false);
    }
  }, [isPayoutSigned, isEditing, isSubmitting, maxJobsExceeded]);

  // Submit timesheet function reads from the context which is updated by the shifts
  // component when user edits their shifts.
  const handleSubmitTimesheet = () => {
    // Validate signature
    const validation = validateSignature();
    if (!validation.valid) {
      alert(validation.error);
      return;
    }

    // Get signature data
    const signatureData = getSignatureData();

    const dataToSubmit = {
      worker: worker,
      weekEnding: weekEnding,
      payoutData: payoutTimesheetDays,
      signature: signatureData,
      payoutComments: payoutComments.current || "",
    };

    handleSubmitTimesheetBase(dataToSubmit);
  };

  if (!worker) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Spinner size="h-12 w-12" className="text-green-900" />
      </div>
    );
  }

  document.title = `Payout: ${worker.first} ${worker.last} - ${weekEnding}`;
  appName.current = "Payout Timesheet";

  return (
    <PayoutTimesheetContext.Provider value={payoutTimesheetCtx}>
      <div className="max-w-[1024px] min-w-[640px]  mx-auto px-2 md:px-4 mb-96 font-inter caret-transparent bg-custom-rsbg flex flex-col items-center justify-center p-4">
        {/* Back Button */}
        <div className="w-full mb-4">
          <button
            onClick={() => navigate("/timekeeping/payout-timesheets")}
            className="flex text-sm font-semibold items-center text-green-900 hover:text-green-800 transition-colors"
          >
            <svg
              className="w-5 h-5 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Back to Payout Timesheets
          </button>
        </div>

        {reviewBanner.show ? (
          <div className="w-full mb-2">
            <WarningBanner variant={reviewBanner.variant}>
              {reviewBanner.message}
            </WarningBanner>
          </div>
        ) : null}

        <Title worker={worker} className={"mb-2"} />
        <PayoutShifts
          worker={worker}
          weekEnding={weekEnding}
          reviewStatus={reviewStatus}
          isEditing={isEditing}
          setIsEditing={setIsEditing}
          shiftsToRender={payoutTimesheetDays}
          setShiftsToRender={handleShiftsUpdate}
          onAddShift={addShiftToDay}
          onRemoveShift={removeShiftFromDay}
          className={"mb-2"}
        />

        {/* Payout Comments Section */}
        <div className="w-full mb-2 p-2 py-1 bg-gray-200 rounded-md">
          <label
            htmlFor="payout-comments"
            className="block text-sm font-semibold text-gray-700"
          >
            Payout Comments
          </label>
          <textarea
            id="payout-comments"
            rows={2}
            className="w-full caret-black text-sm px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 resize-vertical"
            placeholder="Add any comments about this timesheet..."
            onChange={handleCommentsChange}
            value={payoutCommentText}
          />
        </div>

        <Checklist
          // TODO: Update backend to send timesheet and hourslog load confirmations as top level vars
          workerTimesheet={payoutTimesheetDays.length > 0}
          hoursLog={payoutTimesheetDays.length > 0}
          hasConflicts={payoutTimesheetCtx.hoursConflicts.current}
          missingLogs={payoutTimesheetCtx.missingLogs.current}
          isEditing={isEditing}
          isPayoutSigned={isPayoutSigned}
          isSubmittable={isSubmittable}
          setIsSubmittable={setIsSubmittable}
          maxJobsExceeded={maxJobsExceeded}
          className={"w-full mb-2 bg-gray-200 rounded-md min-h-[52px]"}
        />
        <BasicCard
          bg="bg-gray-200"
          text="I certify that I have reviewed this timesheet and believe the hours recorded are accurate and complete. I confirm that any reported overtime was necessary and that the employee’s recorded meal and rest breaks were taken or reported. I am not aware of any unreported work-related injuries or illnesses."
          className="font-bold text-sm w-full mb-3 p-2"
        />

        {/* Signature Section */}
        <div className="w-full mb-2 flex flex-col md:flex-row gap-4">
          {/* Worker Signature */}
          <div className="flex-1">
            <div className="border border-gray-300 rounded-t-lg overflow-hidden">
              <div className="border-b font-bold border-gray-300 bg-gray-200 p-2 text-center">
                Worker Signature
              </div>
              <div className="p-0 bg-white h-64 flex items-center justify-center">
                {workerSignature ? (
                  <img
                    src={workerSignature}
                    alt="Worker Signature"
                    className="max-w-full max-h-full object-contain"
                  />
                ) : (
                  <span className="text-gray-500">No signature provided</span>
                )}
              </div>
            </div>
            {/* Button is for even heights on both signatures and only displays name */}
            <Button
              text={`${worker.first} ${worker.initial ? worker.initial + " " : ""}${worker.last}`}
              variant={"disabled"}
              className="flex-1 rounded-t-none"
            />
          </div>

          {/* Manager Signature */}
          <div className="flex-1">
            <div
              className="border border-gray-300 rounded-t-lg overflow-hidden"
              ref={signatureContainerRef}
            >
              <div className="border-b font-bold border-gray-300 bg-gray-200 p-2 text-center">
                Manager Signature
              </div>
              <div className="flex flex-col items-center bg-gray-100">
                <SignatureCanvas
                  ref={payoutSignatureRef}
                  options={signatureCanvasOptions}
                  onEnd={onSignatureEnd}
                  canvasProps={signatureCanvasCanvasProps}
                />
              </div>
            </div>
            <Button
              text="Clear"
              variant={isPayoutSigned ? "warning" : "disabled"}
              className="flex-1 rounded-t-none"
              onClick={clearSignature}
            />
          </div>
        </div>
        <Button
          text={
            isSubmitting
              ? ""
              : showSuccessModal
                ? "Submitted"
                : "Submit Timesheet"
          }
          icon={isSubmitting ? "spinner" : undefined}
          variant={
            showSuccessModal
              ? "disabled"
              : isSubmittable
                ? "primary"
                : "disabled"
          }
          className="mb-4 w-full"
          onClick={handleSubmitTimesheet}
        />
        <ModalWrapper
          isOpen={showSuccessModal}
          onClose={handleSuccessModalClose}
        >
          <div className="text-center p-4">
            <div className="mb-4">
              <div className="text-green-600 text-6xl mb-4">✓</div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                {submissionResult?.isRevision
                  ? "Payout Revision Saved!"
                  : "Timesheet Submitted Successfully!"}
              </h2>
              <p className="text-gray-600 mb-4">
                {submissionResult?.isRevision
                  ? "The payout timesheet revision has been saved and the PDF has been regenerated."
                  : "The payout timesheet has been successfully reviewed and approved."}
              </p>
              <p className="text-gray-600 mb-6">
                You will be returned to the payout timesheet list when you press OK.
              </p>
            </div>
            <Button
              text="OK"
              variant="primary"
              className="w-full"
              onClick={handleSuccessModalClose}
            />
          </div>
        </ModalWrapper>
      </div>
    </PayoutTimesheetContext.Provider>
  );
};

export default PayoutTimesheet;
